import React, { useRef, useEffect } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

const LANGUAGES = [
  { code: "english", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "marathi", name: "Marathi", nativeName: "मराठी", flag: "🇮🇳" },
];

const ChatBotHeader = ({
  onClose,
  onMinimize,
  onResetChat,
  isFullscreen,
  onToggleFullscreen,
  documentContext,
  onClearDocument,
  selectedLanguage,
  onLanguageChange,
}) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === selectedLanguage) || LANGUAGES[0];

  return (
    <div className={`relative bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 text-white p-4 flex items-center justify-between shadow-md ${isFullscreen ? "rounded-none" : "rounded-t-2xl"}`}>
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/30 animate-pulse-slow">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-orange-600 animate-ping" />
        </div>
        <div>
          <h2 className="font-semibold text-white text-sm font-header tracking-wide">BOQ AI</h2>
          <p className="text-xs text-orange-100/90">MyBOQ Assistant</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Language Selector - Improved */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-white/20 rounded-full hover:bg-white/30 transition-all border border-white/30 shadow-sm"
            title="Select language / भाषा निवडा"
            aria-label="Select language"
            aria-haspopup="true"
            aria-expanded={isOpen}
          >
            <span className="text-lg">{currentLang.flag}</span>
            <span className="hidden sm:inline-flex items-center gap-1">
              {currentLang.nativeName}
              <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>

          {isOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl shadow-2xl border border-slate-200 py-1 z-50 animate-fade-in-up"
              role="menu"
            >
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    onLanguageChange(lang.code);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-all ${
                    selectedLanguage === lang.code
                      ? "bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 font-medium"
                      : "text-slate-700 hover:bg-slate-50 hover:text-orange-600"
                  }`}
                  role="menuitemradio"
                  aria-checked={selectedLanguage === lang.code}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="flex flex-col">
                    <span className="font-medium">{lang.nativeName}</span>
                    <span className="text-xs text-slate-400">{lang.name}</span>
                  </div>
                  {selectedLanguage === lang.code && (
                    <svg className="w-5 h-5 ml-auto text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {documentContext && (
          <button
            onClick={onClearDocument}
            className="px-2.5 py-1 text-[10px] font-medium text-white/90 bg-white/15 rounded-full hover:bg-white/25 transition-all flex items-center gap-1"
            title="Clear document context"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Document
          </button>
        )}

        <button
          onClick={onResetChat}
          className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-all flex items-center justify-center"
          title="New chat"
          aria-label="Start new chat"
        >
          <svg className="w-4 h-4 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        <button
          onClick={onToggleFullscreen}
          className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-all flex items-center justify-center"
          title={isFullscreen ? "Exit full screen" : "Full screen"}
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          aria-pressed={isFullscreen}
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4 text-white/90" /> : <Maximize2 className="w-4 h-4 text-white/90" />}
        </button>

        <button
          onClick={onMinimize}
          className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-all flex items-center justify-center"
          title="Minimize"
          aria-label="Minimize chat"
        >
          <svg className="w-4 h-4 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
          </svg>
        </button>

        <button
          onClick={onClose}
          className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-all flex items-center justify-center"
          title="Close"
          aria-label="Close chat"
        >
          <svg className="w-4 h-4 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatBotHeader;